import { useHasAccess } from 'lib-shared-access-control';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import PageSkeleton from 'app/views/layout/components/pageSkeleton';
import { ActivityLog } from 'components/activityLog/components/activityLog';
import { ActivityLog as ActivityLogOld } from 'components/activityLog_old/components';
import { ConversationMessages } from 'components/conversation/components/ConversationMessages';
import Conversation from 'components/conversation/containers/conversation';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import { LayoutView } from './layout.view';
import { AnalyticsRoutes } from './views/analytics';
import { ContactsRoutes } from './views/contacts';
import { ConversationsView } from './views/conversations';
import { DashboardRoutes } from './views/dashboard';
import { DraftsView } from './views/drafts';
import {
  KnowledgeDeliveryRoutes,
  KnowledgeManagementRoutes,
} from './views/knowledge';
import { NotFoundView } from './views/notFound';
import { NotificationsView } from './views/notifications';
import { SearchRoutes } from './views/search';
import { TeamView } from './views/team';

const SettingsRoutes = lazy(() => import('./views/settings'));

/** The layout routes of the application. */
export const LayoutRoutes = (): JSX.Element => {
  const flags = useFeatureFlag(['knowledge-access', 'audit-log']);

  const hasKnowledgeReadAccess = useHasAccess('knowledge.content:r');

  const hasKnowledgeWriteAccess = useHasAccess('knowledge.content:w');

  return (
    <LayoutView>
      <Routes>
        <Route path="/" element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard/*" element={<DashboardRoutes />} />

        <Route path="conversations" element={<ConversationsView />} />

        <Route path="conversation/:csid" element={<Conversation />}>
          <Route index element={<ConversationMessages />} />
          <Route
            path="activity-log"
            element={flags.auditLog ? <ActivityLog /> : <ActivityLogOld />}
          />
        </Route>

        <Route path="team" element={<TeamView />} />
        <Route path="analytics/*" element={<AnalyticsRoutes />} />
        <Route path="notifications" element={<NotificationsView />} />
        <Route path="drafts" element={<DraftsView />} />

        {flags.knowledgeAccess && (
          <>
            {hasKnowledgeWriteAccess && (
              <Route
                path="knowledge/*"
                element={<KnowledgeManagementRoutes />}
              />
            )}
            {hasKnowledgeReadAccess && (
              <Route
                path="internal-kb/*"
                element={<KnowledgeDeliveryRoutes />}
              />
            )}
          </>
        )}

        <Route path="contacts/*" element={<ContactsRoutes />} />
        <Route path="search/*" element={<SearchRoutes />} />
        <Route
          path="settings/*"
          element={
            <Suspense fallback={<PageSkeleton />}>
              <SettingsRoutes />
            </Suspense>
          }
        />

        <Route
          path="*"
          element={
            <NotFoundView navigatePath="/conversations" name="conversations" />
          }
        />
      </Routes>
    </LayoutView>
  );
};
